/**
 * Dialog — модалка (Radix Dialog). Наш стиль: arrow-const + ref-проп.
 * Используется для подтверждений опасных действий (ADMIN_SPEC §5).
 */

import type {ComponentPropsWithoutRef, ComponentRef, HTMLAttributes, Ref} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {X} from 'lucide-react';

import {cn} from '@lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

type OverlayProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Overlay>>;
};
export const DialogOverlay = ({className, ref, ...props}: OverlayProps) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
);

type ContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** Показывать встроенный крестик закрытия (по умолчанию да). */
  showClose?: boolean;
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Content>>;
};
export const DialogContent = ({
  className,
  children,
  showClose = true,
  ref,
  ...props
}: ContentProps) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-card p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:rounded-lg',
        className,
      )}
      {...props}
    >
      {children}
      {showClose && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
          <X className="size-4" />
          <span className="sr-only">Закрыть</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
);

export const DialogHeader = ({className, ...props}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />
);

export const DialogFooter = ({className, ...props}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
    {...props}
  />
);

type TitleProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Title>>;
};
export const DialogTitle = ({className, ref, ...props}: TitleProps) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
);

type DescriptionProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Description>>;
};
export const DialogDescription = ({className, ref, ...props}: DescriptionProps) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
);
